import { navigate } from '@reach/router';
import { Formik, FormikHelpers } from 'formik';
import React, { useRef, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { UploadDesignToNetSuiteModal } from '../UploadDesignToNetSuiteModal';
import { DataForEditDesignBillOfMaterialsForm } from './dataForEditDesignBillOfMaterialsForm';
import { EditDesignBillOfMaterialsForm, NextStepAfterSavingBillOfMaterials } from './EditDesignBillOfMaterialsForm';
import {
  EditDesignBillOfMaterialsFormModel,
  EditDesignBillOfMaterialsFormModelValidator,
  getInitialEditDesignBillOfMaterialsFormModel,
} from './editDesignBillOfMaterialsFormModel';
import {
  mapEditDesignBillOfMaterialsFormModelToCommand,
  UpdateDesignBillOfMaterialsCommand,
} from './updateDesignBillOfMaterialsCommand';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; designId: string }>;

export const EditDesignBillOfMaterials = ({ projectId, designId, project }: Props) => {
  const getDataRequest = useApiRequest<DataForEditDesignBillOfMaterialsForm>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}/designs/${designId}/data-for-update-bill-of-materials-form`,
  });

  const updateBillOfMaterialsRequest = useApiRequest<undefined, UpdateDesignBillOfMaterialsCommand>({
    httpMethod: 'put',
    endpointUrl: `projects/${projectId}/designs/${designId}/bill-of-materials`,
  });

  const [isUploadToNetSuiteModalOpen, setIsUploadToNetSuiteModalOpen] = useState(false);
  const nextStepAfterSave = useRef<NextStepAfterSavingBillOfMaterials>('BackToProject');

  const onSubmit = (
    formModel: EditDesignBillOfMaterialsFormModel,
    formikHelpers: FormikHelpers<EditDesignBillOfMaterialsFormModel>,
  ) => {
    const command = mapEditDesignBillOfMaterialsFormModelToCommand(
      formModel,
      assertNotNull(getDataRequest.response).designId,
    );
    updateBillOfMaterialsRequest.sendRequest({
      requestBody: command,
      onSuccess: () => {
        formikHelpers.setSubmitting(false);

        if (nextStepAfterSave.current === 'UploadToNetSuite') {
          setIsUploadToNetSuiteModalOpen(true);
        } else {
          void navigate(projectUrls.projectDetails(project.projectId));
        }
      },
      onFailure: () => formikHelpers.setSubmitting(false),
    });
  };

  return (
    <GetRequest getDataRequest={getDataRequest}>
      {({ response: data }) => {
        const editBillOfMaterialsFormModelValidator = new EditDesignBillOfMaterialsFormModelValidator(data.partsById);

        return (
          <>
            <UploadDesignToNetSuiteModal
              projectId={project.projectId}
              designId={data.designId}
              isOpen={isUploadToNetSuiteModalOpen}
              closeModal={() => navigate(projectUrls.projectDetails(project.projectId))}
            />
            <BreadcrumbBar>
              <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
              <BreadcrumbSeparator />
              <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
                {project.projectReference}
              </BreadcrumbLink>
              <BreadcrumbSeparator />
              <div>{data.designName}</div>
              <BreadcrumbSeparator />
              <div>Bill of Materials</div>
            </BreadcrumbBar>
            <ProjectHeader project={project} />
            <PageContainer>
              <AssertUserHasPermission requiredPermission="Edit">
                <Header1>Bill of Materials</Header1>
                <Formik<EditDesignBillOfMaterialsFormModel>
                  onSubmit={onSubmit}
                  initialValues={getInitialEditDesignBillOfMaterialsFormModel(data)}
                  validate={formModel => editBillOfMaterialsFormModelValidator.validate(formModel)}
                >
                  {formProps => (
                    <EditDesignBillOfMaterialsForm
                      {...formProps}
                      data={data}
                      setNextStepAfterSave={nextStep => {
                        nextStepAfterSave.current = nextStep;
                      }}
                    />
                  )}
                </Formik>
                {updateBillOfMaterialsRequest.error && <FormErrorBox error={updateBillOfMaterialsRequest.error} />}
              </AssertUserHasPermission>
            </PageContainer>
          </>
        );
      }}
    </GetRequest>
  );
};
